/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for integrated buttons and
 * Klaviyo subscriptions for email signup forms.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;
    /** @type {boolean} */
    this.isSubmitting = false;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.submitButton = this.querySelector('.email-signup__button');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');
    this.form = this.querySelector('form');
    this.errorContainer = this.querySelector('.email-signup__message--error');
    this.successContainer = this.querySelector('.email-signup__message--success');
    this.errorMessage = this.querySelector('.email-signup__message-error-text');

    this.klaviyoCompanyId = this.dataset.klaviyoCompanyId || '';
    this.klaviyoListId = this.dataset.klaviyoListId || '';
    this.klaviyoSource = this.dataset.klaviyoSource || 'Email Signup';
    this.errorFallbackMessage = this.dataset.errorFallbackMessage || 'Something went wrong. Please try again.';

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');
    const hasKlaviyoConfig = this.klaviyoCompanyId && this.klaviyoListId;

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    if (this.form && hasKlaviyoConfig) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }

  validateEmail() {
    if (!(this.input instanceof HTMLInputElement)) return false;

    const email = this.input.value.trim();
    if (!email) {
      this.showError('Please enter your email address.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.showError('Please enter a valid email address.');
      return false;
    }

    return true;
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting) return;
    if (!this.validateEmail()) return;

    this.hideError();
    this.hideSuccess();
    this.setLoadingState(true);
    this.isSubmitting = true;

    try {
      const email = this.input.value.trim();
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.klaviyoSource,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.klaviyoListId,
              },
            },
          },
        },
      };

      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoCompanyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-02-15',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok && response.status !== 202) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.errors?.[0]?.detail || this.errorFallbackMessage;
        throw new Error(message);
      }

      this.form?.reset();
      this.showSuccess();
    } catch (error) {
      const message = error instanceof Error ? error.message : this.errorFallbackMessage;
      this.showError(message);
    } finally {
      this.setLoadingState(false);
      this.isSubmitting = false;
    }
  }

  setLoadingState(isLoading) {
    this.form?.classList.toggle('is-submitting', isLoading);
    if (this.submitButton instanceof HTMLButtonElement) {
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('is-loading', isLoading);
    }
  }

  showError(message) {
    if (!(this.errorContainer instanceof HTMLElement)) return;

    if (this.errorMessage instanceof HTMLElement) {
      this.errorMessage.textContent = message;
    } else {
      this.errorContainer.textContent = message;
    }

    this.errorContainer.hidden = false;
    this.errorContainer.focus();
  }

  hideError() {
    if (!(this.errorContainer instanceof HTMLElement)) return;
    this.errorContainer.hidden = true;
  }

  showSuccess() {
    if (!(this.successContainer instanceof HTMLElement)) return;
    this.successContainer.hidden = false;
    this.successContainer.focus();
  }

  hideSuccess() {
    if (!(this.successContainer instanceof HTMLElement)) return;
    this.successContainer.hidden = true;
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
